import React, { useEffect, useRef, useState } from "react";
import { Button, Input, message } from "antd";
import { ArrowLeftOutlined, MailOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { request_password_reset } from "../../Services/user.service";
import { VERDICTIA_WORDMARK_DARK_SRC } from "../../brand/verdictiaBrand";
import "./password-reset.css";

function ForgotPassword() {
    const { t } = useTranslation("login");
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const redirectTimerRef = useRef(null);

    const genericMessage = t("reset.request.generic");

    useEffect(() => {
        return () => {
            if (redirectTimerRef.current) {
                window.clearTimeout(redirectTimerRef.current);
            }
        };
    }, []);

    const showSuccessAndReturnToLogin = () => {
        setSubmitted(true);
        message.success(genericMessage);
        if (redirectTimerRef.current) {
            window.clearTimeout(redirectTimerRef.current);
        }
        redirectTimerRef.current = window.setTimeout(() => {
            navigate("/", { replace: true });
        }, 1800);
    };

    const handleSubmit = async () => {
        const normalizedEmail = email.trim().toLowerCase();
        if (!normalizedEmail) {
            message.error(t("validation.email_required"));
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
            message.error(t("validation.invalid_email"));
            return;
        }

        setLoading(true);
        try {
            await request_password_reset(normalizedEmail);
            showSuccessAndReturnToLogin();
        } catch (error) {
            if (error?.response?.status === 429) {
                message.error(t("reset.request.rate_limited"));
            } else {
                showSuccessAndReturnToLogin();
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <main className="auth-reset-page">
            <section className="auth-reset-card" aria-labelledby="forgot-password-title">
                <button className="auth-reset-back" type="button" onClick={() => navigate("/")}>
                    <ArrowLeftOutlined />
                    <span>{t("reset.back_to_login")}</span>
                </button>

                <img className="auth-reset-logo" src={VERDICTIA_WORDMARK_DARK_SRC} alt="Verdictia" />

                <div className="auth-reset-copy">
                    <p className="auth-reset-kicker">{t("reset.request.kicker")}</p>
                    <h1 id="forgot-password-title">{t("reset.request.title")}</h1>
                    <p>{t("reset.request.subtitle")}</p>
                </div>

                <div className="auth-reset-form">
                    <label htmlFor="password-reset-email">{t("reset.request.email_label")}</label>
                    <Input
                        id="password-reset-email"
                        size="large"
                        prefix={<MailOutlined />}
                        value={email}
                        placeholder={t("reset.request.email_placeholder")}
                        onChange={(event) => setEmail(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === "Enter") {
                                handleSubmit();
                            }
                        }}
                        disabled={loading || submitted}
                    />

                    {submitted && (
                        <div className="auth-reset-status" role="status">
                            {genericMessage}
                        </div>
                    )}

                    <Button
                        className="auth-reset-primary"
                        type="primary"
                        size="large"
                        block
                        onClick={handleSubmit}
                        loading={loading}
                        disabled={submitted}
                    >
                        {submitted ? t("reset.request.sent_button") : t("reset.request.submit")}
                    </Button>
                </div>
            </section>
        </main>
    );
}

export default ForgotPassword;
